/**
 * src/services/webrtcPolicyService.ts
 *
 * Client bindings for reading + writing the per-country WebRTC
 * live-session policy triplet.
 *
 * Storage:
 *   - On-chain source of truth: `geo-compliance::get_webrtc_config /
 *     set_webrtc_config`
 *     (see contracts/v2/geo-compliance/src/lib.rs).  The three fields
 *     `webrtc_policy`, `webrtc_session_cost`, and
 *     `webrtc_max_duration_secs` live inside CountryPolicyV2 so every
 *     compliance read pulls the WebRTC state atomically.
 *   - Backend mirror:
 *       GET  /api/admin/webrtc-policy/:country  (full snapshot)
 *       POST /api/admin/webrtc-policy           (full snapshot; legacy
 *                                                 single-field `value`
 *                                                 is still accepted)
 *
 * Tri-state policy values:
 *   0 = NotSet     — compliance default_allow_all wins; paid sessions
 *                    refuse to start until an admin picks a value.
 *   1 = Allowed    — sessions may start once the cost (if any) is paid.
 *   2 = Disallowed — sessions are refused outright.
 */

export const WEBRTC_POLICY = {
  NOT_SET:    0,
  ALLOWED:    1,
  DISALLOWED: 2,
} as const;

export type WebRtcPolicyValue = typeof WEBRTC_POLICY[keyof typeof WEBRTC_POLICY];

/**
 * Full snapshot of the per-country WebRTC policy, including the backend
 * fallback duration (`effectiveMaxDurationSeconds`) so callers can
 * always show the operator a realistic time cap without having to
 * duplicate the fallback logic client-side.
 *
 * V2 fields are optional on this interface so clients that only need
 * the V1 triplet compile unchanged.  The backend always returns them
 * (with zero-semantics defaults for un-migrated countries), but
 * older admin UIs reading this will simply ignore them.
 */
export interface WebRtcPolicyConfig {
  policy: WebRtcPolicyValue;
  /** Raw i128 amount in stroops, preserved as a string to avoid precision loss. */
  sessionCost: string;
  /** 0 means "contract did not set an explicit limit" — see `effectiveMaxDurationSeconds`. */
  maxDurationSeconds: number;
  /** Always a positive value: falls back to the backend default when the contract returns 0. */
  effectiveMaxDurationSeconds: number;
  usingFallbackDuration: boolean;
  policyVersion: number;
  source?: 'mirror' | 'unset' | string;

  // ── V2 fields (additive) ────────────────────────────────────────────────────
  /** Whether viewers may pay to extend a live session.  Default true. */
  extensionsEnabled?: boolean;
  /** Raw stroops string.  '0' / '' means "reuse `sessionCost`". */
  extensionCost?: string;
  /** Stored seconds per extension; 0 means "reuse `maxDurationSeconds`". */
  extensionDurationSeconds?: number;
  /** Backend-derived cost to actually charge. */
  effectiveExtensionCost?: string;
  /** Backend-derived seconds the next extension will add. */
  effectiveExtensionSeconds?: number;
  /** Hard cap on total session length across all extensions.  0 = unlimited. */
  maxTotalDurationSeconds?: number;
  effectiveMaxTotalDurationSeconds?: number;
  /** Hard cap on number of extensions per session.  0 = unlimited. */
  maxExtensions?: number;
  effectiveMaxExtensions?: number;
  /** Minimum seconds an extension must add after clamping.  0 = no floor. */
  minExtensionSeconds?: number;
  effectiveMinExtensionSeconds?: number;
  /** Basis points routed to the platform treasury (0..10_000). */
  platformBps?: number;
  /** Basis points routed directly to the broadcaster (0..10_000). */
  broadcasterBps?: number;
}

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${DEFAULT_BACKEND}${path}`, {
    ...(init || {}),
    headers: {
      'Content-Type': 'application/json',
      ...((init?.headers as Record<string, string>) || {}),
    },
  });
  const data = await resp.json().catch(() => ({} as any));
  if (!resp.ok || data.ok === false) {
    // Surface backend `details` (typically `{ wallet, country }`) so
    // the admin UI can explain "wallet GXXX is not the country admin
    // for country YYY" instead of a generic "save_failed".
    const detail = data?.details ? ` (${JSON.stringify(data.details)})` : '';
    const hint   = (resp.status === 401 || resp.status === 403)
      ? ' — make sure your connected wallet is the country admin (or a global admin) for this country.'
      : '';
    throw new Error((data.error || `request_failed_${resp.status}`) + detail + hint);
  }
  return data as T;
}

export async function getWebRtcPolicyConfig(country: number): Promise<WebRtcPolicyConfig> {
  try {
    const data = await request<any>(`/api/admin/webrtc-policy/${country}`);
    const policy = Number(data.policy ?? data.value ?? 0);
    const asStr = (v: unknown, fallback = '0') =>
      typeof v === 'string' ? v : (v == null ? fallback : String(v));
    const asNum = (v: unknown, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };
    return {
      policy: (policy === 1 || policy === 2 ? policy : 0) as WebRtcPolicyValue,
      sessionCost: asStr(data.sessionCost),
      maxDurationSeconds: asNum(data.maxDurationSeconds),
      effectiveMaxDurationSeconds: asNum(
        data.effectiveMaxDurationSeconds ?? data.maxDurationSeconds, 0,
      ) || 3600,
      usingFallbackDuration: Boolean(data.usingFallbackDuration),
      policyVersion: asNum(data.policyVersion),
      source: data.source,

      // V2 fields — default to sensible zero-semantics if the backend
      // is a pre-V2 build (the GET endpoint now emits them, but keep
      // the client tolerant to stale servers).
      extensionsEnabled:
        typeof data.extensionsEnabled === 'boolean' ? data.extensionsEnabled : true,
      extensionCost:              asStr(data.extensionCost, ''),
      extensionDurationSeconds:   asNum(data.extensionDurationSeconds),
      effectiveExtensionCost:     asStr(data.effectiveExtensionCost, asStr(data.sessionCost)),
      effectiveExtensionSeconds:  asNum(
        data.effectiveExtensionSeconds ?? data.effectiveMaxDurationSeconds,
      ) || 3600,
      maxTotalDurationSeconds:    asNum(data.maxTotalDurationSeconds),
      effectiveMaxTotalDurationSeconds: asNum(data.effectiveMaxTotalDurationSeconds),
      maxExtensions:              asNum(data.maxExtensions),
      effectiveMaxExtensions:     asNum(data.effectiveMaxExtensions),
      minExtensionSeconds:        asNum(data.minExtensionSeconds),
      effectiveMinExtensionSeconds: asNum(data.effectiveMinExtensionSeconds),
      platformBps:                asNum(data.platformBps),
      broadcasterBps:             asNum(data.broadcasterBps),
    };
  } catch {
    return {
      policy: WEBRTC_POLICY.NOT_SET,
      sessionCost: '0',
      maxDurationSeconds: 0,
      effectiveMaxDurationSeconds: 3600,
      usingFallbackDuration: true,
      policyVersion: 0,
      source: 'unset',
      extensionsEnabled: true,
      extensionCost: '0',
      extensionDurationSeconds: 0,
      effectiveExtensionCost: '0',
      effectiveExtensionSeconds: 3600,
      maxTotalDurationSeconds: 0,
      effectiveMaxTotalDurationSeconds: 0,
      maxExtensions: 0,
      effectiveMaxExtensions: 0,
      minExtensionSeconds: 0,
      effectiveMinExtensionSeconds: 0,
      platformBps: 0,
      broadcasterBps: 0,
    };
  }
}

/** Legacy helper for callers that only need the tri-state flag. */
export async function getWebRtcPolicy(country: number): Promise<WebRtcPolicyValue> {
  const cfg = await getWebRtcPolicyConfig(country);
  return cfg.policy;
}

export interface SetWebRtcPolicyInput {
  walletAddress?: string;
  policy: WebRtcPolicyValue;
  /** String stroops to keep i128 precision. Use "0" for free. */
  sessionCost: string;
  maxDurationSeconds: number;
  policyVersion?: number;

  // ── V2 fields (optional).  Any omitted field is preserved from the
  //    current mirror state, so partial updates from the admin UI do
  //    not reset previously-set values.
  extensionsEnabled?: boolean;
  extensionCost?: string;
  extensionDurationSeconds?: number;
  maxTotalDurationSeconds?: number;
  maxExtensions?: number;
  minExtensionSeconds?: number;
  platformBps?: number;
  broadcasterBps?: number;
}

export async function setWebRtcPolicyConfig(country: number, input: SetWebRtcPolicyInput): Promise<void> {
  await request(`/api/admin/webrtc-policy`, {
    method: 'POST',
    body: JSON.stringify({ country, ...input }),
  });
}

/** Legacy single-field setter.  Prefer `setWebRtcPolicyConfig`. */
export async function setWebRtcPolicy(country: number, value: WebRtcPolicyValue): Promise<void> {
  await request(`/api/admin/webrtc-policy`, {
    method: 'POST', body: JSON.stringify({ country, value }),
  });
}

export function policyLabel(value: WebRtcPolicyValue): string {
  switch (value) {
    case WEBRTC_POLICY.ALLOWED: return 'Allowed';
    case WEBRTC_POLICY.DISALLOWED: return 'Disallowed';
    default: return 'Not set (default)';
  }
}

// ─── Display helpers ─────────────────────────────────────────────────────────

/** Format a stroops string as a human-readable XLM amount with up to 7 decimals. */
export function stroopsToDisplay(stroops: string | number): string {
  const raw = typeof stroops === 'string' ? stroops : String(stroops);
  if (!/^-?\d+$/.test(raw)) return '0';
  const negative = raw.startsWith('-');
  const digits = negative ? raw.slice(1) : raw;
  const padded = digits.padStart(8, '0');
  const whole = padded.slice(0, -7).replace(/^0+(?=\d)/, '') || '0';
  const frac  = padded.slice(-7).replace(/0+$/, '');
  const formatted = frac ? `${whole}.${frac}` : whole;
  return negative ? `-${formatted}` : formatted;
}

/** Parse a user-entered XLM display amount back into a stroops string. */
export function displayToStroops(display: string): string {
  const trimmed = (display || '').trim();
  if (!trimmed) return '0';
  if (!/^\d+(\.\d{1,7})?$/.test(trimmed)) throw new Error('Invalid amount');
  const [whole, frac = ''] = trimmed.split('.');
  const paddedFrac = frac.padEnd(7, '0').slice(0, 7);
  // Avoid BigInt literals (`10_000_000n`) so the file compiles under the
  // project's pre-ES2020 TypeScript target.  `BigInt(string)` is supported
  // at runtime wherever `BigInt` itself is available.
  const stroops = (BigInt(whole) * BigInt('10000000') + BigInt(paddedFrac)).toString();
  return stroops;
}

/** Format a seconds count as `hh:mm:ss` / `mm:ss` / `0s`. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m ${s.toString().padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, '0')}s`;
  return `${s}s`;
}
